import { useMemo } from 'react';
import PropTypes from 'prop-types';

import { Flex, Spacer } from '@zillow/constellation';
import {
  Button,
  ButtonGroup,
  Card,
  Divider,
  IconButton,
  Menu,
  Paragraph,
  Tag,
  Tooltip,
} from '@zillow/constellation-10';
import { IconMoreFilled } from '@zillow/constellation-icons';
import { LoadingButton, LoadingParagraph, LoadingText } from '@zg-rentals/loading-components';

import { VERTICAL_DISPLAY_TYPE } from '../../../../constants/PropertiesConstants';
import SimpleCard from '../../../SimpleCard';
import ConnectedStatus from '../../../ConnectedStatus';
import WhoHasSignedDescription from '../WhoHasSignedDescription';

import { NoUnderlineAnchor, singleTextLineStyles } from '../../../../styles';

const LeaseCard = ({ lease, index, displayType, isLoading }) => {
  const {
    leaseStatus,
    leaseName,
    leaseDescription,
    ctaButton,
    overflowButtons,
    signeeList,
    descriptionTooltip,
    dateRange,
    latestExtension,
  } = lease;

  const leaseDisplay = leaseName || `Lease #${index + 1}`;

  const handlePrimaryCTAClick = (event) => {
    event.preventDefault();

    /**
     * do not update location if the user clicks on WhoHasSignedDescription
     *
     * if the displayType is SIMPLE then the closest element to the event target will be a button
     * it's the WhoHasSignedDescription component
     */
    const closestButton = event.target.closest('button') || null;
    const path = event.currentTarget.href || null;

    if (!path || closestButton) {
      return;
    }

    window.router.updateLocation(path);
  };

  const renderMenu = (menuItems) => {
    if (menuItems && menuItems.length) {
      return menuItems.map(({ text, url, newTab }, i) => (
        <Menu.Item asChild key={`anchor-${i}`}>
          <a href={url} target={newTab ? '_blank' : '_self'} rel={newTab ? 'noopener noreferrer' : undefined}>
            <Menu.ItemLabel>{text}</Menu.ItemLabel>
          </a>
        </Menu.Item>
      ));
    }

    return null;
  };

  const renderTooltip = (tooltipConfig) => {
    if (!tooltipConfig) {
      return null;
    }

    return (
      <Tooltip.Root>
        <Tooltip.Trigger />
        <Tooltip.Portal>
          <Tooltip.Content>
            <Paragraph textStyle="body-bold" css={{ paddingBottom: '200' }}>
              {tooltipConfig.title}
            </Paragraph>
            <Paragraph textStyle="body-sm">{tooltipConfig.text}</Paragraph>
            <Tooltip.Arrow />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    );
  };

  const renderDescription = (description, whoHasSignedList = []) => {
    if (isLoading || (description && description.type && description.text)) {
      switch (description?.type) {
        case 'active':
          return (
            <Spacer marginTop="sm">
              <WhoHasSignedDescription signeeList={whoHasSignedList} />
            </Spacer>
          );
        case 'caution':
          return (
            <Paragraph
              data-test="connected-caution-description"
              textStyle="body-sm"
              css={{ marginTop: '200', color: 'text.feedback.critical.strong' }}
            >
              {description.text}
            </Paragraph>
          );
        default:
          return (
            <LoadingParagraph
              data-test="connected-neutral-description"
              textStyle="body-sm"
              title={description?.text}
              isLoading={isLoading}
              loadingWidth="20%"
              css={{ marginTop: '200' }}
            >
              {description?.text} {renderTooltip(descriptionTooltip)}
            </LoadingParagraph>
          );
      }
    }

    return null;
  };

  const cardContent = useMemo(() => {
    return (
      <>
        <LoadingParagraph
          textStyle="body-sm-bold"
          title={leaseDisplay}
          isLoading={isLoading}
          loadingWidth="100%"
          css={{ marginBottom: '200', ...singleTextLineStyles, fontSize: { lg: 'body' } }}
        >
          {leaseDisplay}
        </LoadingParagraph>
        {latestExtension && (
          <>
            <Flex display="flex" gap="xs" flexWrap="wrap">
              <ConnectedStatus message={latestExtension?.status?.text} type={latestExtension?.status.type} size="sm" />
              {latestExtension && (
                <Tag tone="info" size="sm">
                  Lease renewal
                </Tag>
              )}
            </Flex>
            {latestExtension?.dateRange && (
              <Paragraph textStyle="body-sm" css={{ marginTop: '200' }}>
                {latestExtension.dateRange}
              </Paragraph>
            )}
            {renderDescription(latestExtension?.description, signeeList)}
            {displayType === VERTICAL_DISPLAY_TYPE.FULL && (
              <>
                <ButtonGroup aria-label="lease renewal action buttons" css={{ marginTop: '400' }}>
                  <Button asChild tone="brand" emphasis="filled" fluid={true} onClick={handlePrimaryCTAClick}>
                    <a href={latestExtension?.ctaButton.url}>{latestExtension?.ctaButton.text}</a>
                  </Button>
                  {latestExtension?.overflowButtons?.length > 0 && (
                    <Menu
                      content={renderMenu(latestExtension?.overflowButtons)}
                      trigger={<IconButton fluid={true} tone="brand" emphasis="outlined" icon={<IconMoreFilled />} />}
                    />
                  )}
                </ButtonGroup>
                <Divider css={{ marginBlock: '400' }} />
                <Paragraph
                  textStyle="body-sm-bold"
                  css={{
                    marginBottom: '200',
                    ...singleTextLineStyles,
                  }}
                >
                  Original lease
                </Paragraph>
              </>
            )}
          </>
        )}
        {(displayType === VERTICAL_DISPLAY_TYPE.FULL ||
          (displayType === VERTICAL_DISPLAY_TYPE.SIMPLE && !latestExtension)) && (
          <>
            <Flex display="flex" gap="xs" flexWrap="wrap">
              {isLoading ? (
                <LoadingText textStyle="body-xs" loadingWidth="20%" isLoading={true} />
              ) : (
                <ConnectedStatus message={leaseStatus.text} type={leaseStatus.type} size="sm" />
              )}
            </Flex>
            {dateRange && (
              <Paragraph textStyle="body-sm" css={{ marginTop: '200' }}>
                {dateRange}
              </Paragraph>
            )}
            {renderDescription(leaseDescription, signeeList)}
          </>
        )}
        {displayType === VERTICAL_DISPLAY_TYPE.FULL && (
          <ButtonGroup aria-label="leasing action buttons" css={{ marginTop: '400' }}>
            {isLoading ? (
              <Flex display="flex" justifyContent="space-between" flex="1">
                <LoadingButton isLoading={true} loadingWidth="90%">
                  Loading
                </LoadingButton>
                <LoadingButton isLoading={true} loadingWidth="9%">
                  Loading
                </LoadingButton>
              </Flex>
            ) : (
              <>
                <Button asChild tone="brand" emphasis="filled" fluid onClick={handlePrimaryCTAClick}>
                  <a
                    href={ctaButton.url}
                    target={ctaButton?.newTab ? '_blank' : '_self'}
                    rel={ctaButton?.newTab ? 'noopener noreferrer' : undefined}
                  >
                    {ctaButton.text}
                  </a>
                </Button>
                {overflowButtons?.length > 0 && (
                  <Menu
                    content={renderMenu(overflowButtons)}
                    trigger={<IconButton fluid={true} tone="brand" emphasis="outlined" icon={<IconMoreFilled />} />}
                  />
                )}
              </>
            )}
          </ButtonGroup>
        )}
      </>
    );
  }, [isLoading]);

  const renderCard = () => {
    if (displayType === VERTICAL_DISPLAY_TYPE.SIMPLE) {
      return (
        <SimpleCard isDisabled={true} className="LeaseCard" cursor="pointer" withChevron>
          {cardContent}
        </SimpleCard>
      );
    }

    return (
      <Card className="LeaseCard" tone="neutral" elevated={false}>
        {cardContent}
      </Card>
    );
  };

  const withAnchor = ({ ctaButton, latestExtension }) => {
    return (
      <NoUnderlineAnchor
        target="_self"
        href={latestExtension ? latestExtension?.ctaButton?.url : ctaButton?.url}
        onClick={handlePrimaryCTAClick}
      >
        {renderCard()}
      </NoUnderlineAnchor>
    );
  };

  return (
    <>{displayType === VERTICAL_DISPLAY_TYPE.SIMPLE ? withAnchor({ ctaButton, latestExtension }) : renderCard()}</>
  );
};

LeaseCard.propTypes = {
  lease: PropTypes.shape({
    leaseId: PropTypes.number,
    leaseStatus: PropTypes.shape({
      text: PropTypes.string,
      type: PropTypes.string,
    }),
    leaseThumbnailUrl: PropTypes.string,
    leaseName: PropTypes.string,
    leaseDescription: PropTypes.shape({
      type: PropTypes.string,
      text: PropTypes.string,
    }),
    descriptionTooltip: PropTypes.shape({
      title: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    }),
    ctaButton: PropTypes.shape({
      text: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    }),
    overflowButtons: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
    ),
    signeeList: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string.isRequired,
        text: PropTypes.string.isRequired,
      }),
    ),
  }).isRequired,
  isMultipleLeases: PropTypes.bool,
  index: PropTypes.number,
  displayType: PropTypes.string,
};

LeaseCard.defaultProps = {
  isMultipleLeases: false,
  index: '',
  displayType: VERTICAL_DISPLAY_TYPE.FULL,
};

export default LeaseCard;
